import { clamp, map, times } from 'lodash';
import { MultiplexMogPeakDetectionChannelProfile } from './types';

export const spotIllustrationWeakPeakPosition = 0.16;
export const spotIllustrationStrongPeakPosition = 0.68;

export const spotIllustrationSignalAt = (position: number) => {
  const peak = (center: number, width: number, height: number) =>
    height * Math.exp(-0.5 * ((position - center) / width) ** 2);
  return clamp(
    0.015 + peak(0.16, 0.025, 0.015) + peak(0.44, 0.11, 0.28) + peak(0.68, 0.05, 0.6) + peak(0.9, 0.025, 0.985),
    0,
    1
  );
};

export const buildSpotIllustrationSignal = (sampleCount = 100) =>
  map(times(sampleCount), (index) => {
    const position = index / (sampleCount - 1);
    return { position, value: spotIllustrationSignalAt(position) };
  });

export type CellSpotOutcome = 'eligible' | 'signal' | 'distance';
export const cellIllustrationDrawingAxis = 34;
export const cellIllustrationMajorAxisUm = 20;
export const cellIllustrationNucleusDiameterUm = 10;

export const cellIllustrationSpots = [
  { label: 'A', xUm: 5, yUm: 4, intensity: 0.02, background: 0.01, noiseUnits: 1 },
  { label: 'B', xUm: 3, yUm: 12, intensity: 0.035, background: 0.01, noiseUnits: 6 },
  { label: 'C', xUm: 14, yUm: 4, intensity: 0.28, background: 0.02, noiseUnits: 2.8 },
  { label: 'D', xUm: 13.5, yUm: 13.5, intensity: 0.78, background: 0.04, noiseUnits: 8 },
  { label: 'E', xUm: 14, yUm: 13.5, intensity: 0.52, background: 0.03, noiseUnits: 6 },
  { label: 'F', xUm: 10, yUm: 8, intensity: 0.15, background: 0.04, noiseUnits: 5.5 },
];

export const buildCellSpotIllustration = (
  profile: MultiplexMogPeakDetectionChannelProfile,
  workingUmpp?: number | null
) => {
  const hasResolution = workingUmpp != null && Number.isFinite(workingUmpp) && workingUmpp > 0;
  const cutoff = profile.minContrast;
  const lowerBound = profile.clipMinFraction * profile.clipMaxFraction;
  const upperBound = profile.clipMaxFraction;
  const prepare = (value: number) => clamp((value - lowerBound) / (upperBound - lowerBound), 0, 1);
  const spots = map(cellIllustrationSpots, (spot) => {
    const preparedPeak = prepare(spot.intensity);
    const preparedBackground = prepare(spot.background);
    const signal = preparedPeak - preparedBackground;
    const outcome: CellSpotOutcome = signal < cutoff ? 'signal' : 'eligible';
    return {
      ...spot,
      x: (spot.xUm * cellIllustrationDrawingAxis) / cellIllustrationMajorAxisUm,
      y: (spot.yUm * cellIllustrationDrawingAxis) / cellIllustrationMajorAxisUm,
      expressionOpacity: Number((0.1 + 0.9 * spot.intensity).toFixed(3)),
      preparedPeak,
      preparedBackground,
      signal,
      outcome: outcome as CellSpotOutcome,
      distanceOutcome: outcome as CellSpotOutcome,
      distanceToStronger: Infinity,
      strongerLabel: null as string | null,
    };
  });
  const ranked = [...spots].sort((firstSpot, secondSpot) => secondSpot.intensity - firstSpot.intensity);
  const accepted: typeof spots = [];
  for (const spot of ranked) {
    if (spot.outcome !== 'eligible' || !hasResolution) continue;
    spot.distanceToStronger = Math.min(
      ...map(
        accepted,
        (stronger) => Math.max(Math.abs(spot.xUm - stronger.xUm), Math.abs(spot.yUm - stronger.yUm)) / workingUmpp!
      )
    );
    spot.strongerLabel =
      accepted.find(
        (stronger) =>
          Math.max(Math.abs(spot.xUm - stronger.xUm), Math.abs(spot.yUm - stronger.yUm)) / workingUmpp! ===
          spot.distanceToStronger
      )?.label ?? null;
    if (spot.distanceToStronger < profile.minDistancePx) spot.distanceOutcome = 'distance';
    else accepted.push(spot);
  }
  return {
    spots,
    workingUmpp: hasResolution ? workingUmpp! : null,
    cellMajorAxisPixels: hasResolution ? cellIllustrationMajorAxisUm / workingUmpp! : null,
    spacingDrawingUnits: hasResolution
      ? (profile.minDistancePx * workingUmpp! * cellIllustrationDrawingAxis) / cellIllustrationMajorAxisUm
      : null,
    cutoff,
    lowerBound,
    upperBound,
    clippingFactor: profile.clipMinFraction,
  };
};

export const spotDetectionExampleMaxima = { 8: 240, 16: 3000 } as const;
export type SpotMeshOutcome = CellSpotOutcome | 'small' | 'large';

export const buildSpotDetectionMeshModel = (
  profile: MultiplexMogPeakDetectionChannelProfile,
  workingUmpp: number | null | undefined,
  bitDepth: 8 | 16 | null
) => {
  const illustration = buildCellSpotIllustration(profile, workingUmpp);
  const referenceMax = bitDepth == null ? null : spotDetectionExampleMaxima[bitDepth];
  const radiusByLabel: Record<string, number> = { A: 0.12, B: 0.18, C: 0.5, D: 0.6, E: 0.42, F: 0.3 };
  const spots = illustration.spots.map((spot) => {
    const radiusUm = radiusByLabel[spot.label];
    let outcome: SpotMeshOutcome = spot.distanceOutcome;
    if (outcome === 'eligible') {
      if (profile.minRadiusUm != null && radiusUm < profile.minRadiusUm) outcome = 'small';
      else if (profile.maxRadiusUm != null && radiusUm > profile.maxRadiusUm) outcome = 'large';
    }
    return {
      ...spot,
      radiusUm,
      outcome,
      rawPeak:
        referenceMax == null
          ? null
          : clamp(spot.intensity, illustration.lowerBound, illustration.upperBound) * referenceMax,
      rawContrast:
        referenceMax == null ? null : spot.signal * (illustration.upperBound - illustration.lowerBound) * referenceMax,
    };
  });
  return {
    ...illustration,
    spots,
    bitDepth,
    referenceMax,
    rawLowerBound: referenceMax == null ? null : illustration.lowerBound * referenceMax,
    rawUpperBound: referenceMax == null ? null : illustration.upperBound * referenceMax,
    rawMinimumContrast:
      referenceMax == null
        ? null
        : profile.minContrast * (illustration.upperBound - illustration.lowerBound) * referenceMax,
  };
};

export type SpotDetectionMeshModel = ReturnType<typeof buildSpotDetectionMeshModel>;
export interface SpotMeshLabelAnchor {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  text: string;
}
export const placeSpotMeshLabels = (anchors: SpotMeshLabelAnchor[], width: number, height: number) => {
  const placed: Array<SpotMeshLabelAnchor & { left: number; top: number }> = [];
  const padding = 8;
  for (const anchor of anchors) {
    const offsets = [
      [16, -44],
      [-44, -44],
      [22, 12],
      [-48, 12],
    ];
    for (let radius = 64; radius <= Math.max(width, height); radius += 32) {
      for (const [horizontal, vertical] of [
        [1, -1],
        [-1, -1],
        [1, 1],
        [-1, 1],
        [0, -1],
        [0, 1],
        [1, 0],
        [-1, 0],
      ]) {
        offsets.push([horizontal * radius, vertical * radius]);
      }
    }
    const candidates = offsets.map(([horizontal, vertical]) => ({
      ...anchor,
      left: Math.max(padding, Math.min(width - anchor.width - padding, anchor.x + horizontal)),
      top: Math.max(padding, Math.min(height - anchor.height - padding, anchor.y + vertical)),
    }));
    const result =
      candidates.find((candidate) =>
        placed.every(
          (other) =>
            candidate.left + candidate.width + padding <= other.left ||
            other.left + other.width + padding <= candidate.left ||
            candidate.top + candidate.height + padding <= other.top ||
            other.top + other.height + padding <= candidate.top
        )
      ) ?? candidates[0];
    placed.push(result);
  }
  return placed;
};

export const spotDetectionMeshSignalAt = (horizontalUm: number, verticalUm: number, model: SpotDetectionMeshModel) =>
  clamp(
    model.spots.reduce((signal, spot) => {
      const squaredDistance = (horizontalUm - spot.xUm) ** 2 + (verticalUm - spot.yUm) ** 2;
      return signal + spot.intensity * Math.exp((-Math.LN2 * squaredDistance) / spot.radiusUm ** 2);
    }, 0.012),
    model.lowerBound,
    model.upperBound
  );
